import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const ProjectStats = () => {
  const [stats, setStats] = useState({
    loading: true,
    totalProjects: 0,
    highestJobNumber: null,
    completedProjects: 0,
    takeoffProjects: 0,
    projectsWithRevisions: 0,
    sampleJobNumbers: []
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get total projects count
        const { count: totalCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true });

        // Get highest job number
        const { data: maxJob } = await supabase
          .from('projects')
          .select('job_number')
          .order('job_number', { ascending: false })
          .limit(1);

        // Get completed projects count
        const { count: completedCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('modeling_status', 'Complete');

        // Get sample of job numbers to understand the pattern
        const { data: sampleJobs } = await supabase
          .from('projects')
          .select('job_number, name')
          .order('job_number', { ascending: false })
          .limit(20);

        // Count projects with revision in name
        const { data: revisionProjects } = await supabase
          .from('projects')
          .select('job_number, name')
          .ilike('name', '%revision%');

        // Get takeoff projects count
        const { count: takeoffCount } = await supabase
          .from('takeoff_projects')
          .select('*', { count: 'exact', head: true });

        setStats({
          loading: false,
          totalProjects: totalCount || 0,
          highestJobNumber: maxJob?.[0]?.job_number || null,
          completedProjects: completedCount || 0,
          takeoffProjects: takeoffCount || 0,
          projectsWithRevisions: revisionProjects?.length || 0,
          sampleJobNumbers: sampleJobs || []
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, []);

  if (stats.loading) {
    return <div>Loading project statistics...</div>;
  }

  // Calculate estimated completed projects (highest job number - 1000)
  const estimatedCompleted = stats.highestJobNumber ? stats.highestJobNumber - 1000 : 0;

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>Project Statistics Analysis</h2>
      
      <h3>Database Counts:</h3>
      <ul>
        <li>Total Projects in Database: {stats.totalProjects}</li>
        <li>Projects with "Complete" Status: {stats.completedProjects}</li>
        <li>Projects with "Revision" in Name: {stats.projectsWithRevisions}</li>
        <li>Takeoff Projects: {stats.takeoffProjects}</li>
      </ul>

      <h3>Job Number Analysis:</h3>
      <ul>
        <li>Highest Job Number: {stats.highestJobNumber}</li>
        <li>Estimated Completed (Job# - 1000): {estimatedCompleted}</li>
      </ul>

      <h3>Recent Job Numbers:</h3>
      <table style={{ borderCollapse: 'collapse', marginTop: '10px' }}>
        <thead>
          <tr>
            <th style={{ border: '1px solid #ccc', padding: '5px' }}>Job Number</th>
            <th style={{ border: '1px solid #ccc', padding: '5px' }}>Project Name</th>
          </tr>
        </thead>
        <tbody>
          {stats.sampleJobNumbers.slice(0, 10).map((project, idx) => (
            <tr key={idx}>
              <td style={{ border: '1px solid #ccc', padding: '5px' }}>{project.job_number}</td>
              <td style={{ border: '1px solid #ccc', padding: '5px' }}>{project.name}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Recommendation:</h3>
      <p>
        Based on the data:
        <br />- Total projects in database: {stats.totalProjects}
        <br />- Using highest job number - 1000: {estimatedCompleted} completed
        <br />- Projects marked as complete: {stats.completedProjects}
        <br />- Takeoff projects (separate system): {stats.takeoffProjects}
        <br />- Projects with "revision" in name: {stats.projectsWithRevisions}
      </p>
    </div>
  );
};

export default ProjectStats;